import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectClinicId } from "../../../store/slices/authSlice";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import { useGetAppointmentsQuery } from "../../../store/services/calendarApi";
import { useGetPatientsQuery } from "../../../store/services/patientsApi";
import { useGetPendingCollectionQuery } from "../../../store/services/financeApi";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  ChartLine,
  Users,
  Calendar,
  CurrencyCircleDollar,
  Clock,
  Timer,
  CheckCircle,
  ClockCountdown,
  X,
  Check,
  Hourglass,
  UserPlus,
  UsersThree,
  FileText,
  ArrowRight,
} from "@phosphor-icons/react";
import { StatItem, StatItemGrid, HelpButton } from "../../../components/shared";
import "./ClinicSnapshotPage.css";

// Get week boundaries (Sunday to Saturday)
function getWeekBoundaries(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  
  // Start of week (Sunday at 00:00:00)
  const startOfWeek = new Date(d);
  startOfWeek.setDate(d.getDate() - day);
  startOfWeek.setHours(0, 0, 0, 0);
  
  // End of week (Saturday at 23:59:59)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return { startOfWeek, endOfWeek };
}

// Get month boundaries
function getMonthBoundaries(date = new Date()) {
  const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  return { startOfMonth, endOfMonth };
}

// Generate nice colors for therapists
const THERAPIST_COLORS = [
  "#4dd9b4", // Primary turquoise
  "#3b82f6", // Blue
  "#8b5cf6", // Purple
  "#f59e0b", // Amber
  "#ec4899", // Pink
  "#22c55e", // Green
  "#ef4444", // Red
  "#06b6d4", // Cyan
  "#f97316", // Orange
  "#6366f1", // Indigo
];

function ClinicSnapshotPage() {
  const navigate = useNavigate();
  const clinicId = useSelector(selectClinicId);

  // Tabs state
  const [appointmentsTab, setAppointmentsTab] = useState("future"); // 'future' | 'past'

  // Date ranges
  const { startOfWeek, endOfWeek } = useMemo(() => getWeekBoundaries(), []);
  const { startOfMonth, endOfMonth } = useMemo(() => getMonthBoundaries(), []);
  const now = useMemo(() => new Date(), []);

  // ==========================================
  // DATA FETCHING
  // ==========================================

  // Fetch all therapists in clinic
  const { data: therapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Fetch all appointments for the week (no therapistId filter = all clinic appointments)
  const { data: weekAppointmentsRaw = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: null, // All therapists
      startDate: startOfWeek.toISOString(),
      endDate: endOfWeek.toISOString(),
    },
    { skip: !clinicId }
  );

  // Fetch all appointments for the month (for income stats)
  const { data: monthAppointmentsRaw = [] } = useGetAppointmentsQuery(
    {
      clinicId,
      therapistId: null,
      startDate: startOfMonth.toISOString(),
      endDate: endOfMonth.toISOString(),
    },
    { skip: !clinicId }
  );

  // Blocked time (חסימת זמן) is a calendar visual, not a meeting — keep it out
  // of every snapshot counter and chart.
  const weekAppointments = useMemo(
    () => weekAppointmentsRaw.filter((apt) => apt.isBlockedTime !== true),
    [weekAppointmentsRaw]
  );
  const monthAppointments = useMemo(
    () => monthAppointmentsRaw.filter((apt) => apt.isBlockedTime !== true),
    [monthAppointmentsRaw]
  );

  // Fetch all patients in clinic (active)
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId: null, status: "active", pageSize: 500 },
    { skip: !clinicId }
  );

  // Fetch patients created this month
  const { data: allPatientsForMonth } = useGetPatientsQuery(
    { clinicId, therapistId: null, pageSize: 500 },
    { skip: !clinicId }
  );

  // Fetch pending collection (for all therapists - null means all)
  const { data: pendingCollection = [] } = useGetPendingCollectionQuery(
    { clinicId, therapistId: null, limit: 100 },
    { skip: !clinicId }
  );

  // Active therapists
  const activeTherapists = therapists.filter(t => t.isActive !== false);

  // ==========================================
  // COMPUTED VALUES
  // ==========================================

  // All patients count
  const activePatientsCount = patientsData?.patients?.length || 0;

  // New patients this month
  const newPatientsThisMonth = useMemo(() => {
    if (!allPatientsForMonth?.patients) return 0;
    return allPatientsForMonth.patients.filter((p) => {
      const createdAt = p.createdAt?.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
      return createdAt >= startOfMonth && createdAt <= endOfMonth;
    }).length;
  }, [allPatientsForMonth?.patients, startOfMonth, endOfMonth]);

  // Split appointments into future and past
  const { futureAppointments, pastAppointments } = useMemo(() => {
    const future = [];
    const past = [];

    weekAppointments.forEach((apt) => {
      const aptTime = new Date(apt.startTime);
      if (aptTime >= now) {
        future.push(apt);
      } else {
        past.push(apt);
      }
    });

    // Sort by date
    future.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    past.sort((a, b) => new Date(b.startTime) - new Date(a.startTime)); // Most recent first

    return { futureAppointments: future, pastAppointments: past };
  }, [weekAppointments, now]);

  // Total appointments this week
  const totalWeekAppointments = weekAppointments.filter(
    (apt) => apt.status !== "cancelled"
  ).length;

  // Total pending collection amount
  const totalPendingAmount = useMemo(() => {
    return pendingCollection.reduce((sum, tx) => sum + (tx.amount || 0), 0);
  }, [pendingCollection]);

  // Calculate total income this month from appointments
  const totalIncomeThisMonth = useMemo(() => {
    return monthAppointments.reduce((sum, apt) => {
      // Count only paid appointments
      if (apt.paymentStatus === "paid" && apt.actualPaidAmount) {
        return sum + apt.actualPaidAmount;
      }
      // Backwards compatibility with old isPaid flag
      if (apt.isPaid === true && apt.paymentAmount) {
        return sum + apt.paymentAmount;
      }
      return sum;
    }, 0);
  }, [monthAppointments]);

  // Stats per therapist
  const therapistStats = useMemo(() => {
    return activeTherapists.map((therapist, index) => {
      const therapistAppointments = weekAppointments.filter(
        (apt) => apt.therapistId === therapist.uid && apt.status !== "cancelled"
      );
      const therapistPatients = patientsData?.patients?.filter(
        (p) => p.therapistId === therapist.uid
      ) || [];

      // Count appointments that need action (past appointments missing approval/payment/summary)
      const pendingActionCount = pastAppointments.filter((apt) => {
        if (apt.therapistId !== therapist.uid || apt.status === "cancelled") return false;
        // Check if any of the post-completion actions are pending
        const needsApproval = apt.isApprovedByTherapist !== true;
        const needsPayment = apt.isTherapistUpdatedPaymentStatus !== true;
        const needsSummary = apt.isTherapistSummarizedTheMeeting !== true;
        return needsApproval || needsPayment || needsSummary;
      }).length;

      return {
        ...therapist,
        color: THERAPIST_COLORS[index % THERAPIST_COLORS.length],
        weekAppointments: therapistAppointments.length,
        activePatients: therapistPatients.length,
        pendingActionCount,
      };
    });
  }, [activeTherapists, weekAppointments, patientsData?.patients, pastAppointments]);

  // ==========================================
  // CHART DATA - Appointments per day per therapist (from start of month to today)
  // ==========================================

  const chartData = useMemo(() => {
    // Create array of days from start of month until today
    const days = [];
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    
    let currentDay = new Date(startOfMonth);
    while (currentDay <= today) {
      days.push({
        date: new Date(currentDay),
        dateStr: currentDay.toLocaleDateString("he-IL", { day: "numeric", month: "numeric" }),
      });
      currentDay.setDate(currentDay.getDate() + 1);
    }

    // Count appointments per day per therapist
    return days.map((day) => {
      const dayData = {
        name: day.dateStr,
        date: day.dateStr,
      };

      activeTherapists.forEach((therapist) => {
        const count = monthAppointments.filter((apt) => {
          const aptDate = new Date(apt.startTime);
          return (
            apt.therapistId === therapist.uid &&
            apt.status !== "cancelled" &&
            aptDate.toDateString() === day.date.toDateString()
          );
        }).length;

        dayData[therapist.uid] = count;
      });

      return dayData;
    });
  }, [startOfMonth, activeTherapists, monthAppointments]);

  // ==========================================
  // HELPERS
  // ==========================================

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("he-IL", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMins = Math.round((end - start) / 60000);
    if (diffMins < 60) return `${diffMins} דק׳`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    if (mins === 0) return `${hours} שע׳`;
    return `${hours}:${mins.toString().padStart(2, "0")} שע׳`;
  };

  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return null;
    return `₪${amount.toLocaleString("he-IL")}`;
  };

  const getTherapistName = (therapistId) => {
    const therapist = therapists.find((t) => t.uid === therapistId);
    return therapist ? `${therapist.firstName} ${therapist.lastName}` : "";
  };

  const getTherapistColor = (therapistId) => {
    const index = activeTherapists.findIndex((t) => t.uid === therapistId);
    return THERAPIST_COLORS[index % THERAPIST_COLORS.length];
  };

  const getConfirmationStatus = (apt) => {
    if (apt.status === "cancelled") {
      return { text: "בוטלה", type: "cancelled" };
    }
    if (apt.whatsappInitialDeclined === true || apt.whatsappReminderDeclined === true) {
      return { text: "נדחתה", type: "declined" };
    }
    if (apt.whatsappInitialConfirmed === true && apt.whatsappReminderConfirmed === true) {
      return { text: "אושרה", type: "confirmed" };
    }
    if (apt.whatsappReminderConfirmed === true) {
      return { text: "אישור תזכורת", type: "reminder" };
    }
    if (apt.whatsappInitialConfirmed === true) {
      return { text: "אישור ראשוני", type: "initial" };
    }
    return { text: "ממתין לאישור", type: "pending" };
  };

  // Past appointment status helpers
  const getApprovalStatus = (apt) => {
    if (apt.isApprovedByTherapist === true) return { text: "אושרה", type: "done" };
    return { text: "ממתין", type: "pending" };
  };

  const getPaymentStatus = (apt) => {
    if (apt.isTherapistUpdatedPaymentStatus === true) {
      if (apt.paymentStatus === "paid") return { text: "שולם", type: "done" };
      if (apt.paymentStatus === "partial") return { text: "חלקי", type: "partial" };
      return { text: "טופל", type: "done" };
    }
    return { text: "ממתין", type: "pending" };
  };

  const getSummaryStatus = (apt) => {
    if (apt.isTherapistSummarizedTheMeeting === true) return { text: "נכתב", type: "done" };
    return { text: "ממתין", type: "pending" };
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="snapshot-page">
      {/* Header */}
      <div className="snapshot-header">
        <div className="snapshot-header-content">
          <ChartLine size={28} weight="fill" className="snapshot-header-icon" />
          <div>
            <h1>תמונת מצב</h1>
            <p>סקירה כוללת של הקליניקה</p>
          </div>
        </div>
        <HelpButton featureKey="admin_snapshot" />
      </div>

      {/* Main Stats Grid */}
      <StatItemGrid columns={6}>
        <StatItem
          icon={Calendar}
          value={totalWeekAppointments.toString()}
          label="פגישות השבוע"
          variant="primary"
        />
        <StatItem
          icon={UsersThree}
          value={activeTherapists.length.toString()}
          label="מטפלים"
          variant="primary"
        />
        <StatItem
          icon={Users}
          value={activePatientsCount.toString()}
          label="מטופלים פעילים"
          variant="purple"
        />
        <StatItem
          icon={UserPlus}
          value={newPatientsThisMonth.toString()}
          label="מטופלים חדשים"
          variant="success"
        />
        <StatItem
          icon={CurrencyCircleDollar}
          value={formatAmount(totalIncomeThisMonth)}
          label="הכנסות החודש"
          variant="success"
        />
        <StatItem
          icon={Hourglass}
          value={formatAmount(totalPendingAmount)}
          label="ממתין לגבייה"
          variant="warning"
        />
      </StatItemGrid>

      {/* Content Grid */}
      <div className="snapshot-grid">
        {/* Appointments Section */}
        <div className="snapshot-card snapshot-appointments">
          <div className="snapshot-card-header">
            <div className="snapshot-card-title">
              <Calendar size={20} weight="fill" />
              <span>פגישות השבוע</span>
            </div>
            <div className="snapshot-tabs">
              <button
                className={`snapshot-tab ${appointmentsTab === "future" ? "active" : ""}`}
                onClick={() => setAppointmentsTab("future")}
              >
                עתידיות
                <span className="snapshot-tab-count">{futureAppointments.length}</span>
              </button>
              <button
                className={`snapshot-tab ${appointmentsTab === "past" ? "active" : ""}`}
                onClick={() => setAppointmentsTab("past")}
              >
                עברו
                <span className="snapshot-tab-count">{pastAppointments.length}</span>
              </button>
            </div>
          </div>

          <div className="snapshot-appointments-list">
            {appointmentsTab === "future" && futureAppointments.length === 0 && (
              <div className="snapshot-empty">
                <Calendar size={24} weight="duotone" />
                <span>אין פגישות עתידיות השבוע</span>
              </div>
            )}

            {appointmentsTab === "past" && pastAppointments.length === 0 && (
              <div className="snapshot-empty">
                <Calendar size={24} weight="duotone" />
                <span>אין פגישות שעברו השבוע</span>
              </div>
            )}

            {appointmentsTab === "future" &&
              futureAppointments.map((apt) => {
                const confirmStatus = getConfirmationStatus(apt);
                const duration = formatDuration(apt.startTime, apt.endTime);
                const amount = formatAmount(apt.paymentAmount);

                return (
                  <div key={apt.id} className={`snapshot-apt-card snapshot-apt-${confirmStatus.type}`}>
                    <div className="snapshot-apt-header">
                      <span className="snapshot-apt-name">{apt.patientName}</span>
                      <span
                        className="snapshot-apt-therapist"
                        style={{ backgroundColor: `${getTherapistColor(apt.therapistId)}20`, color: getTherapistColor(apt.therapistId) }}
                      >
                        {getTherapistName(apt.therapistId)}
                      </span>
                    </div>
                    <div className="snapshot-apt-info">
                      <div className="snapshot-apt-details">
                        <div className="snapshot-apt-detail">
                          <Clock size={11} weight="fill" />
                          <span>{formatDate(apt.startTime)} • {formatTime(apt.startTime)}</span>
                        </div>
                        {duration && (
                          <div className="snapshot-apt-detail">
                            <Timer size={11} weight="fill" />
                            <span>{duration}</span>
                          </div>
                        )}
                        {amount && (
                          <div className="snapshot-apt-detail snapshot-apt-amount">
                            <CurrencyCircleDollar size={11} weight="fill" />
                            <span>{amount}</span>
                          </div>
                        )}
                      </div>
                      <div className={`snapshot-apt-status snapshot-apt-status-${confirmStatus.type}`}>
                        {confirmStatus.type === "confirmed" && <CheckCircle size={11} weight="fill" />}
                        {confirmStatus.type === "initial" && <Check size={11} weight="bold" />}
                        {confirmStatus.type === "reminder" && <CheckCircle size={11} weight="fill" />}
                        {confirmStatus.type === "pending" && <ClockCountdown size={11} weight="fill" />}
                        {(confirmStatus.type === "declined" || confirmStatus.type === "cancelled") && <X size={11} weight="bold" />}
                        <span>{confirmStatus.text}</span>
                      </div>
                    </div>
                  </div>
                );
              })}

            {appointmentsTab === "past" &&
              pastAppointments.map((apt) => {
                const duration = formatDuration(apt.startTime, apt.endTime);
                const amount = formatAmount(apt.paymentAmount);
                const approval = getApprovalStatus(apt);
                const payment = getPaymentStatus(apt);
                const summary = getSummaryStatus(apt);

                return (
                  <div key={apt.id} className="snapshot-apt-card snapshot-apt-past">
                    <div className="snapshot-apt-header">
                      <span className="snapshot-apt-name">{apt.patientName}</span>
                      <span
                        className="snapshot-apt-therapist"
                        style={{ backgroundColor: `${getTherapistColor(apt.therapistId)}20`, color: getTherapistColor(apt.therapistId) }}
                      >
                        {getTherapistName(apt.therapistId)}
                      </span>
                    </div>
                    <div className="snapshot-apt-info">
                      <div className="snapshot-apt-details">
                        <div className="snapshot-apt-detail">
                          <Clock size={11} weight="fill" />
                          <span>{formatDate(apt.startTime)} • {formatTime(apt.startTime)}</span>
                        </div>
                        {duration && (
                          <div className="snapshot-apt-detail">
                            <Timer size={11} weight="fill" />
                            <span>{duration}</span>
                          </div>
                        )}
                        {amount && (
                          <div className="snapshot-apt-detail snapshot-apt-amount">
                            <CurrencyCircleDollar size={11} weight="fill" />
                            <span>{amount}</span>
                          </div>
                        )}
                      </div>
                      <div className="snapshot-apt-past-statuses">
                        <div className={`snapshot-apt-mini-status ${approval.type}`} title="אישור מטפל">
                          <Check size={10} weight="bold" />
                          {approval.type === "done" && <CheckCircle size={8} weight="fill" className="snapshot-mini-check" />}
                        </div>
                        <div className={`snapshot-apt-mini-status ${payment.type}`} title="סטטוס תשלום">
                          <CurrencyCircleDollar size={10} weight="fill" />
                          {payment.type === "done" && <CheckCircle size={8} weight="fill" className="snapshot-mini-check" />}
                        </div>
                        <div className={`snapshot-apt-mini-status ${summary.type}`} title="סיכום פגישה">
                          <FileText size={10} weight="fill" />
                          {summary.type === "done" && <CheckCircle size={8} weight="fill" className="snapshot-mini-check" />}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Therapists Section */}
        <div className="snapshot-card snapshot-therapists">
          <div className="snapshot-card-header">
            <div className="snapshot-card-title">
              <UsersThree size={20} weight="fill" />
              <span>מטפלים</span>
            </div>
          </div>

          <div className="snapshot-therapists-list">
            {therapistStats.map((therapist) => (
              <div key={therapist.uid} className="snapshot-therapist-card">
                <div className="snapshot-therapist-avatar" style={{ backgroundColor: therapist.color }}>
                  {therapist.firstName?.charAt(0)}
                </div>
                <div className="snapshot-therapist-info">
                  <span className="snapshot-therapist-name">
                    {therapist.firstName} {therapist.lastName}
                  </span>
                  <div className="snapshot-therapist-stats">
                    <span className="snapshot-therapist-stat">
                      <Calendar size={12} weight="fill" />
                      {therapist.weekAppointments} פגישות
                    </span>
                    <span className="snapshot-therapist-stat">
                      <Users size={12} weight="fill" />
                      {therapist.activePatients} מטופלים
                    </span>
                    {therapist.pendingActionCount > 0 && (
                      <span className="snapshot-therapist-stat snapshot-therapist-stat-pending">
                        <ClockCountdown size={12} weight="fill" />
                        {therapist.pendingActionCount} ממתינות לטיפול
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {therapistStats.length === 0 && (
              <div className="snapshot-empty">
                <UsersThree size={24} weight="duotone" />
                <span>אין מטפלים בקליניקה</span>
              </div>
            )}
          </div>
        </div>

        {/* Chart Section */}
        <div className="snapshot-card snapshot-chart">
          <div className="snapshot-card-header">
            <div className="snapshot-card-title">
              <ChartLine size={20} weight="fill" />
              <span>פגישות החודש</span>
            </div>
          </div>

          <div className="snapshot-chart-container">
            {activeTherapists.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                    interval={chartData.length > 15 ? Math.floor(chartData.length / 10) : 0}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#6b7280" }}
                    tickLine={false}
                    axisLine={{ stroke: "rgba(0,0,0,0.1)" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "white",
                      border: "none",
                      borderRadius: "6px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      fontSize: "12px",
                      direction: "rtl",
                    }}
                    labelFormatter={(label, payload) => {
                      if (payload && payload[0]) {
                        return payload[0].payload.date;
                      }
                      return label;
                    }}
                    formatter={(value, name) => {
                      const therapist = activeTherapists.find((t) => t.uid === name);
                      const displayName = therapist ? `${therapist.firstName} ${therapist.lastName}` : name;
                      return [value, displayName];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                    formatter={(value) => {
                      const therapist = activeTherapists.find((t) => t.uid === value);
                      return therapist ? `${therapist.firstName} ${therapist.lastName}` : value;
                    }}
                  />
                  {activeTherapists.map((therapist, index) => (
                    <Line
                      key={therapist.uid}
                      type="monotone"
                      dataKey={therapist.uid}
                      stroke={THERAPIST_COLORS[index % THERAPIST_COLORS.length]}
                      strokeWidth={2}
                      dot={{ fill: THERAPIST_COLORS[index % THERAPIST_COLORS.length], strokeWidth: 0, r: 3 }}
                      activeDot={{ r: 5, strokeWidth: 0 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="snapshot-empty">
                <ChartLine size={24} weight="duotone" />
                <span>אין מספיק נתונים להצגת גרף</span>
              </div>
            )}
          </div>
        </div>

        {/* Pending Payments Section */}
        <div className="snapshot-card snapshot-pending">
          <div className="snapshot-card-header">
            <div className="snapshot-card-title">
              <Hourglass size={20} weight="fill" />
              <span>ממתינים לתשלום</span>
            </div>
            <span className="snapshot-badge">{pendingCollection.length}</span>
          </div>

          <div className="snapshot-pending-list">
            {pendingCollection.slice(0, 8).map((tx) => (
              <div key={tx.id} className="snapshot-pending-item">
                <div className="snapshot-pending-info">
                  <span className="snapshot-pending-name">{tx.patientName}</span>
                  <span className="snapshot-pending-date">
                    {tx.expectedPaymentDate
                      ? formatDate(tx.expectedPaymentDate)
                      : "לא צוין תאריך"}
                  </span>
                </div>
                <span className="snapshot-pending-amount">{formatAmount(tx.amount)}</span>
              </div>
            ))}

            {pendingCollection.length === 0 && (
              <div className="snapshot-empty">
                <CheckCircle size={24} weight="duotone" className="snapshot-empty-success" />
                <span>אין תשלומים ממתינים</span>
              </div>
            )}

            {pendingCollection.length > 8 && (
              <div className="snapshot-more">
                +{pendingCollection.length - 8} נוספים
              </div>
            )}
          </div>

          <button className="snapshot-link-btn" onClick={() => navigate("/dashboard/finance")}>
            <ArrowRight size={12} weight="bold" className="snapshot-arrow-rtl" />
            <span>לעמוד הפיננסים</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ClinicSnapshotPage;
